import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { DndProvider } from "react-dnd";
import {
  MultiBackend,
  TouchTransition,
  HTML5DragTransition,
} from "react-dnd-multi-backend";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";

import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { socket } from "./socket";

import { getUserState } from "./app/selector";

import Layout from "./pages/layout/Layout";
import Home from "./pages/home/Home";
import Profile from "./pages/profile/Profile";
import NoPage from "./pages/home/NoPage";
import ForgetPassword from "./pages/home/ForgetPassword";
import ResetPassword from "./pages/home/ResetPassword";
import SearchUser from "./pages/searchUser/SearchUser";
import SendRequest from "./pages/common/request/SendRequest";
import IndividualRating from "./pages/searchUser/individualRating/IndividualRating";
import Gallery from "./pages/searchUser/gallery/Gallery";
import News from "./pages/news/News";
import PhotoSearch from "./pages/searchUser/photoSearch/PhotoSearch";
import Inbox from "./pages/news/inbox/Inbox";
import Requests from "./pages/news/requests/Requests";
import AcceptedRequests from "./pages/news/acceptedRequests/AcceptedRequests";
import Settings from "./pages/settings/Settings";
import Administration from "./pages/settings/Administration";
import NoPermission from "./pages/common/permission/NoPermission";
import Impressum from "pages/layout/footer/Impressum";
import Contact from "pages/layout/footer/Contact";
import AboutUs from "pages/layout/footer/AboutUs";
import FAQ from "pages/layout/footer/Faq";
import AGB from "pages/layout/footer/Agb";
import Privacy from "pages/layout/footer/Privacy";
import VerifyEmail from "pages/home/VerifyEmail";
import ResetPasswordConfirmation from "pages/home/ResetPasswordConfirmation";
import GDPRPopup from "pages/layout/GDPRPopup";

import { WebSocketContext } from "./context";
import { userLogin, stopAuthLoading } from "./features/user/userSlice"; 
import { BACKEND_URL } from "./constants";

// IMPORT THE NEW PROTECTED ROUTE COMPONENT (adjust the path as needed)
import ProtectedRoute from "./ProtectedRoute";

const HTML5toTouch = {
  backends: [
    {
      id: "html5",
      backend: HTML5Backend,
      transition: HTML5DragTransition,
    },
    {
      id: "touch",
      backend: TouchBackend,
      options: { enableMouseEvents: true },
      preview: true,
      transition: TouchTransition,
    },
  ],
};

const App = () => {
  const { loggedIn, user } = useSelector(getUserState);
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(socket.connected);

  // Authentication Check
  useEffect(() => {
    const authenticateUser = async () => {
      const token = Cookies.get("bambkuss-token");
      
      if (!token) {
        dispatch(stopAuthLoading());
        return; 
      }

      axios.defaults.headers.common["Authorization"] = "Bearer " + token;

      if (!loggedIn) {
        try {
          const response = await axios.get(`${BACKEND_URL}/user`);
          dispatch(userLogin({ user: response.data })); 
        } catch (error) {
          console.error("Authentication failed:", error);
          dispatch(stopAuthLoading()); 
        }
      } else {
        dispatch(stopAuthLoading());
      }
    };

    authenticateUser();
  }, [dispatch, loggedIn]);

  // Socket Connections
  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }
    function onDisconnect() {
      setIsConnected(false);
    }
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, []);

  useEffect(() => {
    if (!isConnected) return;
    if (loggedIn && user?.user_id && isConnected) {
      socket.emit("online", { user_id: user.user_id, date: Date.now() });
    }
    return () => {
      if (user?.user_id) {
        socket.emit("offline", { user_id: user.user_id, date: Date.now() });
      }
    };
  }, [user, loggedIn, isConnected]);

  return (
    <WebSocketContext.Provider value={socket}>
      <DndProvider backend={MultiBackend} options={HTML5toTouch}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Layout />}>
              
              {/* ============================== */}
              {/* PUBLIC ROUTES (Accessible to all) */}
              {/* ============================== */}
              <Route index element={<Home />} />
              <Route path="verify" element={<VerifyEmail />} />
              <Route path="reset-confirmation" element={<ResetPasswordConfirmation />} />
              <Route path="forget-password" element={<ForgetPassword />} />
              <Route path="reset-password/:token" element={<ResetPassword />} />
              <Route path="impressum" element={<Impressum />} />
              <Route path="contact" element={<Contact />} />
              <Route path="about-us" element={<AboutUs />} />
              <Route path="faq" element={<FAQ />} />
              <Route path="agb" element={<AGB />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="no-permission" element={<NoPermission />} />
              <Route path="*" element={<NoPage />} />

              {/* ============================== */}
              {/* PRIVATE ROUTES (Requires Login)  */}
              {/* ============================== */}
              <Route element={<ProtectedRoute />}>
                <Route path="profile" element={<Profile />} />
                <Route path="profile/:view_user_id" element={<Profile />} />
                <Route path="profile/:view_user_id/:view_photo_id" element={<Profile />} />
                
                <Route path="search-user" element={<SearchUser />} />
                <Route path="individual-rating" element={<IndividualRating />} />
                <Route path="gallery" element={<Gallery />} />
                
                <Route path="find-user-location" element={<PhotoSearch searchType="location" />} />
                <Route path="find-user-location/:search_location" element={<PhotoSearch searchType="location" />} />
                <Route path="find-username" element={<PhotoSearch searchType="username" />} />
                <Route path="find-username/:search_username" element={<PhotoSearch searchType="username" />} />
                
                <Route path="news" element={<News />} />
                <Route path="inbox" element={<Inbox />} />
                <Route path="inbox/:active_participant_id" element={<Inbox />} />
                <Route path="requests" element={<Requests />} />
                <Route path="accepted-requests" element={<AcceptedRequests />} />
                <Route path="send-request/:photo_id" element={<SendRequest />} />
                
                <Route path="settings" element={<Settings />} />
                <Route path="admin" element={<Administration />} />
              </Route>

            </Route>
          </Routes>
          <GDPRPopup />
        </BrowserRouter>
      </DndProvider>
    </WebSocketContext.Provider>
  );
};

export default App;